import { useState } from 'react'
import type { AddressEditFieldProps } from './AddressEditField'
import { InlineDisplayContent, InlineEdit } from '@ztechapps/webui.input'
import { EditPopOverCard, TooltipPopOver } from '@ztechapps/webui.util'
import { AddressSelectEditContent } from '../Address/AddressSelectEditContent'
import { validateAddress } from '../Address/validateAddress'
import { addressDisplayValue } from '../Address/addressDisplayValue'
import { useAddressEditPopOverContext } from './Context'
import { useAddressEditPopOver } from './useAddressEditPopOver'
import { isEmptyAddressValue } from './isEmptyAddressValue'
import { withDefaultCountry } from './withDefaultCountry'
import styles from './AddressPopoverEditField.module.scss'

// The anchored-card arm. This is the ONE arm that participates in `AddressEditPopOverContext`'s
// single-open-key exclusivity — opening any editor closes any other, discarding its in-progress
// edit (`useAddressEditPopOver`). It renders through `EditPopOverCard` — an `Overlay`/`Popover` shell shaped after the
// shipped `useTaskTooltipPopOver` (libs/task/webui/src/TaskTooltipPopOver/useTaskTooltipPopOver.tsx) —
// rather than the shared `BasePopover`: a light-dismiss, fixed-width table-filter popover and a
// sticky, self-sizing, form-bearing card that must survive an in-progress edit are two different
// things, and `BasePopover` would need `rootClose={false}`, no `setShow`, and an empty `bodyStyle`
// to neutralise its own opinions before this card could use it.
export const AddressPopoverEditField = ({
  editorKey,
  name,
  label,
  initialValues,
  displayAddress,
  mutationConfig,
  updateParams,
  addressSelectComponent,
  addressSelectProps,
  extraActions,
  buildFilter,
  parseCreateOption,
  emptyContent,
  canEdit,
  disabledReason,
}: AddressEditFieldProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const { openKey, open } = useAddressEditPopOverContext()

  // `initialValues` is passed through untouched — it also feeds `isEmptyAddressValue` below, and a
  // genuinely absent address must stay empty so `emptyContent`'s call-to-action renders instead of
  // an `AddressDisplay` stub. The USA default is applied only to the edit surface's `value` further
  // down, never here.
  const { value, setNewValue, saveNow, cancelNow, submitting, error } = useAddressEditPopOver({
    editorKey,
    name,
    initialValues,
    mutationConfig,
    updateParams,
    validate: validateAddress,
  })

  const valueContent = isEmptyAddressValue(value) ? emptyContent : addressDisplayValue(displayAddress ?? value)

  if (canEdit === false) {
    // Mirrors `InlineInput`'s own `canEdit === false` rendering (libs/webui/input/src/InlineInput/
    // index.tsx): a disabled cursor, no muted styling — the address still renders identically to
    // an editable one, but it is not a click target and `open()` never fires, so the card can
    // never appear for a surface editing is not permitted on. The hover text names WHY, not just that
    // it is disabled (`webui-component-usage-guide.md` → a disabled control needs its reason);
    // falls back to the generic string only when the caller hasn't threaded one through yet.
    return (
      <InlineDisplayContent label={label}>
        <TooltipPopOver tooltipBody={disabledReason || 'Editing is disabled'}>
          <span className={styles.disabledAddress}>{valueContent}</span>
        </TooltipPopOver>
      </InlineDisplayContent>
    )
  }

  return (
    <InlineDisplayContent label={label}>
      <span ref={setAnchorEl}>
        <InlineEdit.Display className="mb-0" onClick={() => open(editorKey)}>
          {valueContent}
        </InlineEdit.Display>
      </span>
      <EditPopOverCard id={`${editorKey}-pop-over`} show={openKey === editorKey} target={anchorEl}>
        {/* Id shape (`${editorKey}-pop-over`) matched by the stored browser spec's
            `.popover.show[id$="Address-pop-over"]` locator — keep it exact. */}
        <AddressSelectEditContent
          value={withDefaultCountry(value)}
          setNewValue={setNewValue}
          saveNow={saveNow}
          cancelNow={cancelNow}
          submitting={submitting}
          error={error}
          addressSelectComponent={addressSelectComponent}
          addressSelectProps={addressSelectProps}
          buildFilter={buildFilter}
          extraActions={extraActions}
          parseCreateOption={parseCreateOption}
        />
      </EditPopOverCard>
    </InlineDisplayContent>
  )
}
